import React, { useState } from "react";
import ConsultationForm from "./Consultation"; // adjust path if your pages folder is elsewhere
import PaediatricWard1 from "../assets/images/Paediatric/Paediatric-ward-1.jpg";
import PaediatricWard2 from "../assets/images/Paediatric/Paediatric-ward-2.jpg";
import PaediatricWard3 from "../assets/images/Paediatric/Paediatric-ward-3.webp";
import PaediatricWard4 from "../assets/images/Paediatric/Paediatric-ward-4.jpg";
import PaediatricWard5 from "../assets/images/Paediatric/Paediatric-ward-5.avif";
import PaediatricWard6 from "../assets/images/Paediatric/Paediatric-ward-6.jpg";
import PaediatricWard7 from "../assets/images/Paediatric/Paediatric-ward-7.avif";
import PaediatricWard8 from "../assets/images/Paediatric/Paediatric-ward-8.jpg";
import PaediatricWard9 from "../assets/images/Paediatric/Paediatric-ward-9.avif";
import SEO from "../components/SEO";

/**
 * Paediatric Ward Designs — landing page
 * Same design system as OTSuiteDesigns.jsx — black/white/grey/ash palette,
 * spec-line cards (Zone / Size / Age Group),
 * "Get Free Quote" opens the shared Consultation modal.
 */

const FONT_IMPORT_URL =
  "https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&family=Inter:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap";

const tokens = {
  ink: "#343F86",
  bg: "#ECEEF6",
  card: "#FFFFFF",
  teal: "#3A4060",
  tealDark: "#262E63",
  amber: "#8A8F9C",
  blue: "#5B6072",
  hairline: "#CDD1E0",
  sub: "#5B6072",
};

const paediatricWards = [
  {
    name: "Infant & Toddler Ward Bay",
    zone: "Level 1",
    size: "30 sq.m / 4 cots",
    spec: "0–2 Years",
    tag: "Infant",
    note: "Crib-style beds, rounded furniture edges, parent recliners beside every cot.",
    image: PaediatricWard1,
  },
  {
    name: "General Paediatric Ward",
    zone: "Level 1",
    size: "40 sq.m / 6 beds",
    spec: "2–12 Years",
    tag: "General",
    note: "Themed wall murals, low-height nurse-call buttons, shared play corner.",
    image: PaediatricWard2,
  },
  {
    name: "Paediatric Isolation Room",
    zone: "Level 1",
    size: "18 sq.m",
    spec: "All Ages",
    tag: "Isolation",
    note: "Negative-pressure option, parent observation window, dedicated isolation play kit storage.",
    image: PaediatricWard3,
  },
  {
    name: "Adolescent Ward Room",
    zone: "Level 1",
    size: "20 sq.m / 2 beds",
    spec: "13–18 Years",
    tag: "Adolescent",
    note: "Privacy-focused layout, study/charging desk, age-appropriate decor distinct from younger wards.",
    image: PaediatricWard4,
  },
  {
    name: "Paediatric Oncology Ward",
    zone: "Level 1",
    size: "24 sq.m / 3 beds",
    spec: "All Ages",
    tag: "Oncology",
    note: "Infection-control finishes, family lounge nook, soft non-clinical color palette.",
    image: PaediatricWard5,
  },
  {
    name: "Paediatric Post-Op Recovery Bay",
    zone: "Level 2",
    size: "26 sq.m / 4 beds",
    spec: "All Ages",
    tag: "Post-Op",
    note: "Direct OT corridor link, parent-presence recovery zone, low-stimulation lighting controls.",
    image: PaediatricWard6,
  },
  {
    name: "Paediatric Day-Care Ward",
    zone: "Level 1",
    size: "35 sq.m / 6 bays",
    spec: "All Ages",
    tag: "Day-Care",
    note: "Recliner-style observation bays, quick-turnover layout, dedicated discharge lounge.",
    image: PaediatricWard7,
  },
  {
    name: "NICU Step-Down Paediatric Bay",
    zone: "Level 2",
    size: "20 sq.m / 4 cots",
    spec: "Neonatal",
    tag: "Step-Down",
    note: "Transitional care from NICU, kangaroo-care seating, soft circadian lighting.",
    image: PaediatricWard8,
  },
  {
    name: "Family-Centered Paediatric Suite",
    zone: "Level 2",
    size: "28 sq.m",
    spec: "All Ages",
    tag: "Family Suite",
    note: "Pull-out parent bed, mini play zone, private bathroom for extended-stay comfort.",
    image: PaediatricWard9,
  },
];

function SpecLine({ zone, size, spec }) {
  return (
    <div
      style={{
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: 11.5,
        letterSpacing: 0.3,
        color: tokens.sub,
        display: "flex",
        gap: 10,
        flexWrap: "wrap",
      }}
    >
      <span>ZONE&nbsp;<b style={{ color: tokens.teal }}>{zone}</b></span>
      <span>·</span>
      <span>SIZE&nbsp;<b style={{ color: tokens.ink }}>{size}</b></span>
      <span>·</span>
      <span>{spec}</span>
    </div>
  );
}

function PaediatricWardCard({ item, onGetQuote }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: "relative",
        background: tokens.card,
        borderRadius: 4,
        overflow: "hidden",
        border: `1px solid ${tokens.hairline}`,
        boxShadow: hover
          ? "0 14px 28px -14px rgba(14,124,123,0.35)"
          : "0 2px 6px -2px rgba(43,58,66,0.08)",
        transform: hover ? "translateY(-3px)" : "none",
        transition: "all 0.25s ease",
      }}
    >
      {/* corner bracket signature */}
      <div
        style={{
          position: "absolute", top: 10, left: 10, width: 22, height: 22,
          borderTop: `2px solid ${tokens.teal}`, borderLeft: `2px solid ${tokens.teal}`,
          zIndex: 2,
        }}
      />
      <div style={{ height: 168, position: "relative", background: tokens.tealDark }}>
        {item.image ? (
          <img
            src={item.image}
            alt={item.name}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        ) : (
          <div
            style={{
              width: "100%", height: "100%", display: "flex",
              alignItems: "center", justifyContent: "center",
              color: "rgba(255,255,255,0.55)",
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, letterSpacing: 0.5,
            }}
          >
            ADD IMAGE
          </div>
        )}
        {/* tag chip */}
        <span
          style={{
            position: "absolute", bottom: 10, right: 12,
            fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
            color: "#fff", background: "rgba(0,0,0,0.45)",
            padding: "2px 8px", borderRadius: 2,
          }}
        >
          {item.tag.toUpperCase()}
        </span>
      </div>
      <div style={{ padding: "16px 16px 18px" }}>
        <h3
          style={{
            fontFamily: "'Montserrat', 'Inter', sans-serif", fontWeight: 600, fontSize: 17.5,
            color: tokens.ink, margin: "0 0 6px", lineHeight: 1.25,
          }}
        >
          {item.name}
        </h3>
        <p style={{ fontSize: 13, color: tokens.sub, margin: "0 0 10px", lineHeight: 1.45 }}>
          {item.note}
        </p>
        <SpecLine zone={item.zone} size={item.size} spec={item.spec} />
        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          <button
            onClick={() => onGetQuote(item)}
            style={{
              flex: 1, padding: "9px 10px", fontSize: 12.5, fontWeight: 600,
              fontFamily: "'Montserrat', 'Inter', sans-serif", letterSpacing: 0.2,
              background: tokens.amber, color: tokens.ink,
              border: "none", borderRadius: 3, cursor: "pointer",
            }}
          >
            Get Free Quote
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PaediatricWardDesigns() {
  const [showConsultation, setShowConsultation] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const handleGetQuote = (item) => {
    setSelectedItem(item);
    setShowConsultation(true);
  };

  return (
     <>
    <SEO/>
    <div
      style={{
        background: tokens.bg,
        color: tokens.ink,
        fontFamily: "'Inter', 'Montserrat', sans-serif",
        minHeight: "100vh",
      }}
    >
      <style>{`@import url('${FONT_IMPORT_URL}');
        * { box-sizing: border-box; }
        @media (prefers-reduced-motion: reduce) { * { transition: none !important; } }
        button:focus-visible, a:focus-visible { outline: 2px solid ${tokens.teal}; outline-offset: 2px; }
        .paed-grid-3x3 { grid-template-columns: repeat(3, 1fr); }
        @media (max-width: 900px) { .paed-grid-3x3 { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 600px) { .paed-grid-3x3 { grid-template-columns: 1fr; } }
        .paed-modal-overlay {
          position: fixed; inset: 0; z-index: 2000;
          background: rgba(0, 0, 0, 0.65);
          backdrop-filter: blur(5px);
          display: flex; align-items: center; justify-content: center;
          padding: 1rem;
        }
        .paed-modal-inner {
          position: relative; width: 100%; max-width: 680px;
          max-height: 90vh; overflow-y: auto; border-radius: 18px;
        }
        .paed-modal-close {
          position: absolute; top: 14px; right: 16px; z-index: 10;
          background: #262E63; border: none; color: #fff;
          width: 32px; height: 32px; border-radius: 50%;
          font-size: 20px; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          line-height: 1;
        }
      `}</style>

      {/* spacer so the fixed/sticky page navbar in the host app doesn't overlap the grid */}
      <div style={{ height: 32 }} />

      {/* GRID */}
      <section style={{ maxWidth: 1180, margin: "0 auto", padding: "26px 24px 10px" }}>
        <div
          className="paed-grid-3x3"
          style={{
            display: "grid",
            gap: 22,
            marginTop: 70,
          }}
        >
          {paediatricWards.map((item, i) => (
            <PaediatricWardCard key={i} item={item} onGetQuote={handleGetQuote} />
          ))}
        </div>
      </section>

      {/* Consultation Modal — opens on "Get Free Quote" click */}
      {showConsultation && (
        <div
          className="paed-modal-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setShowConsultation(false); }}
        >
          <div className="paed-modal-inner">
            <button
              className="paed-modal-close"
              onClick={() => setShowConsultation(false)}
              aria-label="Close consultation form"
            >
              ×
            </button>
            <ConsultationForm suite={selectedItem} />
          </div>
        </div>
      )}
    </div>
    </>
  );
}